'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { CREATE_TAG, DELETE_TAG, GET_TAG, GET_TAGS, UPDATE_TAG } from '../tagsQueries'
import { TagFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'

const formSchema = z.object({
  tag_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z
          .object({
            id: z.number().optional(),
            permalink: z.string().optional(),
            meta_title: z.string().optional(),
            meta_description: z.string().optional(),
          })
          .optional(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  images: z.array(z.string()).optional(),
  selected_products: z.array(z.object({ id: z.number() })),
}) satisfies z.ZodType<TagFormValues>

interface TagFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function TagForm({
  data,
  onDirtyChange,
  onRequestClose,
}: TagFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueTag: tagData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_TAG, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<TagFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      tag_profiles: [],
      images: [],
      selected_products: [],
    },
  })

  const [updateTag] = useMutation(UPDATE_TAG, {
    onError: e => {
      toast.error('Failed to update tag')
    },
    onCompleted: () => {
      toast.success('Tag updated successfully')
    },
    refetchQueries: ['GET_TAGS', 'GET_TAG'],
  })

  const [deleteTag] = useMutation(DELETE_TAG, {
    refetchQueries: ['GET_TAGS', 'GET_TAG'],
  })

  const handleDelete = () => {
    if (id) {
      const count = tagData?.product_containers?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteTag({
              variables: { where: { id } },
            })
            toast.success('Tag deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete tag')
          }
        },
        description: count
          ? `This tag is used by ${count} product(s). Deleting it will remove it from those products. This action cannot be undone.`
          : 'Are you sure you want to delete this tag? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const [createTag] = useMutation(CREATE_TAG, {
    onError: e => {
      toast.error('Failed to create tag')
    },
    onCompleted: response => {
      toast.success('Tag created successfully')
      const newId = response.createNewTag.id
      setId(newId)
    },
    refetchQueries: ['GET_TAGS'],
  })

  function onSubmit(values: TagFormValues) {
    if (id) {
      console.log(values)
      updateTag({
        variables: {
          where: { id },
          tag_profiles: values.tag_profiles.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
        },
      })
    } else {
      createTag({
        variables: {
          tag_profiles: values.tag_profiles.map(p => ({
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (tagData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          tagData.tag_profiles?.filter(
            p => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
              meta_information: {
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        })

        form.reset({
          tag_profiles: allProfiles,
          images: tagData.main_image ? [tagData.main_image.src] : [],
          selected_products: tagData.product_containers,
        })
      } else {
        // For NEW forms, initialize with profiles for all languages
        form.reset({
          tag_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
            meta_information: {
              permalink: '',
              meta_title: '',
              meta_description: '',
            },
          })),
          images: [],
          selected_products: [],
        })
      }
    }
  }, [tagData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Tag</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Tag Name"
                description="This is your public display name."
                control={form.control}
                name={`tag_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`tag_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                control={form.control}
                name={`tag_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                control={form.control}
                name={`tag_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Meta Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                control={form.control}
                name={`tag_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Image"
                description="Upload tag image"
                control={form.control}
                name="images"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
